import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { NavigationStart, Router } from '@angular/router';
import { debounceTime, filter, fromEvent, Subscription, tap } from 'rxjs';
import { TypeOfResult } from 'src/app/models/typeOfResult/typeOfResult';
import { Role } from 'src/app/models/user/role.model';
import { SearchService } from 'src/app/services/search.service';
import { ISelect, MatMultiSelectComponent } from '../../mat-multiselect/mat-multiselect.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatMultiSelectPaginationComponent } from '../../mat-multiselect/mat-multiselect-pagination.component';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-general-search-form',
    styleUrls: ['./general-search-form.component.scss'],
    templateUrl: './general-search-form.component.html',
    standalone: true,
    imports: [
      MatFormFieldModule,
      CommonModule,
      ReactiveFormsModule,
      MatInputModule,
      FormsModule,
      MatSelectModule,
      MatMenuModule,
      MatIconModule,
      MatMultiSelectPaginationComponent,
      MatMultiSelectComponent,
      MatButtonModule,
      MatTooltipModule
    ]
})
export class GeneralSearchFormComponent implements OnInit, OnDestroy {
  @Input() filterGroup: FormGroup;
  @Input() role: Role | undefined;
  @Input() states: ISelect[] = [];
  @Input() lenders: ISelect[] = [];
  @Input() providers: ISelect[] = [];
  @Input() administrators: ISelect[] = [];
  @Input() industries: ISelect[] = [];
  @Input() products: ISelect[] = [];
  @Input() recordTypes: ISelect[] = [];
  @Output() navigateTo: EventEmitter<{
    id?: number;
    typeOfResult?: TypeOfResult;
  }> = new EventEmitter();
  @Output() applyFilter: EventEmitter<any> = new EventEmitter();

  @ViewChild('filterTrigger') menuTrigger: MatMenuTrigger;

  @ViewChild('optionsContainer') optionsProvidersContainer: ElementRef;
  @ViewChild('optionsAdministratorsContainer') optionsAdministratorsContainer: ElementRef;
  @Input() showLendersDropdown: boolean = false;
  @Input() hasNextPageLenders: boolean = true;
  @Output() loadMoreLenders = new EventEmitter<{ searchKey: string; reset?: boolean }>();

  @Input() hasNextPageProviders: boolean = true;
  @Output() loadMoreProviders = new EventEmitter<{ searchKey: string; reset?: boolean }>();

  @Input() hasNextPageAdministrators: boolean = true;
  @Output() loadMoreAdministrators = new EventEmitter<{ searchKey: string; reset?: boolean }>();

  @Output() onPressedEnter = new EventEmitter<boolean>();
  @Output() onSearchOpened = new EventEmitter<void>();

  private _searchOpened = false;

  statusList: ISelect[] = [
    {
      id: 0,
      name: 'All',
    },
    {
      id: 1,
      name: 'Under Review',
    },
    {
      id: 2,
      name: 'Approved',
    },
    {
      id: 3,
      name: 'Rejected',
    },
    {
      id: 4,
      name: 'Proteris Certified',
    },
    {
      id: 5,
      name: 'Not Certified',
    },
  ];

  submissionProgressList: any[] = [
    {
      id: 0,
      name: 'All',
    },
    {
      id: 1,
      name: 'Partial Submission',
    },
    {
      id: 2,
      name: 'Full Submission',
    },
  ];
  formStatusList: ISelect[] = [
    {
      id: 0,
      name: 'All',
    },
    {
      id: 1,
      name: 'Active',
    },
    {
      id: 2,
      name: 'Inactive',
    },
  ];
  isMenuOpen: boolean = false;

  triggerSearchOpen() {
    if (this._searchOpened) return;
    this._searchOpened = true;
    this.onSearchOpened.emit();
  }

  onMenuOpened() {
    this.isMenuOpen = true;
    this.triggerSearchOpen();
  }

  private routerEventsSubscription?: Subscription;

  constructor(private router: Router, private searchService: SearchService,) {}

  ngOnInit() {
    this.routerEventsSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        this.menuTrigger?.closeMenu();
      });
  }

  ngOnDestroy() {
    this.routerEventsSubscription?.unsubscribe();
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (!this.menuTrigger?.menuOpen) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.search-form, .custom-menu, .cdk-overlay-container')) return;
    this.menuTrigger.closeMenu();
  }

  ngAfterViewInit() {
    if (this.optionsProvidersContainer) {
      fromEvent(this.optionsProvidersContainer.nativeElement, 'scroll')
        .pipe(
          debounceTime(300),
          tap(() => {
            if (this.shouldLoadMoreProviders()) {
              this.loadMoreProviders.emit();
            }
          })
        )
        .subscribe();
    }

    if (this.optionsAdministratorsContainer) {
      fromEvent(this.optionsAdministratorsContainer.nativeElement, 'scroll')
        .pipe(
          debounceTime(300),
          tap(() => {
            if (this.shouldLoadMoreAdministrators()) {
              this.loadMoreAdministrators.emit();
            }
          })
        )
        .subscribe();
    }
  }

  isMenuOpened() {
    return this.isMenuOpen;
  }

  goToSearchView() {
    let params = this.filterGroup.value;
    params.statesIds = (this.filterGroup.controls['statesIds'].value || []).map((x: any) => x.id);
    params.lenderIds = (this.filterGroup.controls['lenderIds'].value || []).map((x: any) => x.id);
    params.productIds = (this.filterGroup.controls['productIds'].value || []).map((x: any) => x.id);
    params.industryIds = (this.filterGroup.controls['industryIds'].value || []).map((x: any) => x.id);
    params.recordTypeIds = (this.filterGroup.controls['recordTypeIds'].value || []).map((x: any) => x.id);


    if(params.administratorId == 0) params.administratorId = null;
    if (params.status === 4) {
      params.proterisCertificationStatusId = 5;
      params.status = null;
    } else if (params.status === 5) {
      params.proterisCertificationStatusId = 6;
      params.status = null;
    } else {
      params.proterisCertificationStatusId = null;
      if (params.status == 0) params.status = null;
    }
    if(params.submissionProgressId == 0) params.submissionProgressId = null;
    if(params.formStatusId == 0) params.formStatusId = null; 
    if (params.submissionProgressId === 1) {
        params.fullSubmission = false;
      } else if (params.submissionProgressId === 2) {
        params.fullSubmission = true;
      } else {
        params.fullSubmission = undefined;
      }    
    if(params.providerOrganizationId == 0) params.providerOrganizationId = null;

    params = { pageSize: 15, ...params };

    this.searchService.updateFilters(params);

    const navigationExtras = { queryParams: params };

    if (this.role === Role.Provider || this.role === Role.Consultant || this.role === Role.ProviderReadOnly) {
      this.router.navigate(['/provider/search/list'], navigationExtras);
    } else if (this.role === Role.Lender || this.role === Role.LenderReadOnly) {
      this.router.navigate(['/lender/search/list'], navigationExtras);
    } else {
      this.router.navigate(['/admin/search/list'], navigationExtras);
    }

  }


  getSearchValue() {
    return this.filterGroup.controls['searchKey'].value;
  }

  getSearchControl() {
    return this.filterGroup.controls['searchKey'] as FormControl;
  }

  onPressEnter() {
    this.onPressedEnter.emit(true);
    this.menuTrigger.closeMenu();
    this.goToSearchView();
  }

  onApplyFilter() {
    this.applyFilter.emit();
    this.goToSearchView()
  }

  onClearFilters(event: Event) {
    event.stopPropagation();
    this.filterGroup.patchValue({
      statesIds: [],
      lenderIds: [],
      status: null,
      submissionProgressId: null,
      providerOrganizationId: null,
      administratorId: null,
      productIds: [],
      industryIds: [],
      formStatusId: null,
      recordTypeIds: [],
    });
  }

  onLoadMoreLenders(event: any) {
    this.loadMoreLenders.emit(event);
  }

  private shouldLoadMoreProviders(): boolean {
    if (!this.hasNextPageProviders) return false;

    const container = this.optionsProvidersContainer.nativeElement;
    return container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
  }

  private shouldLoadMoreAdministrators(): boolean {
    if (!this.hasNextPageAdministrators) return false;

    const container = this.optionsAdministratorsContainer.nativeElement;
    return container.scrollTop + container.clientHeight >= container.scrollHeight - 100;
  }
}
